import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  createAssignment,
  listAssignments,
  revokeAssignment,
  type AssignmentOut,
} from "../api/assignments";
import { ApiError } from "../api/client";
import { listUsers } from "../api/users";
import type { UserListItem } from "../api/users";
import { ALL_ROLES, ROLE_LABELS, type AcademyRole } from "../types/user";
import { formatMoscowDate, moscowDateToUtcIso } from "../utils/datetime";

interface Props {
  courseId: number;
  canManage: boolean;
}

const MANAGEABLE_TARGET_ROLES: AcademyRole[] = [
  "trainee",
  "guide",
  "mentor",
  "manager",
  "curator",
  "content_manager",
  "admin",
];

export function AssignmentsPanel({ courseId, canManage }: Props) {
  const [items, setItems] = useState<AssignmentOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  // Add-form state
  const [targetType, setTargetType] = useState<"user" | "role">("user");
  const [userQ, setUserQ] = useState("");
  const [userQDebounced, setUserQDebounced] = useState("");
  const [userResults, setUserResults] = useState<UserListItem[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserLabel, setSelectedUserLabel] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<AcademyRole>("trainee");
  const [dueAt, setDueAt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(async () => {
    if (!canManage) return;
    setLoading(true);
    setError(null);
    try {
      const list = await listAssignments(courseId);
      setItems(list.filter((a) => a.revoked_at === null));
    } catch (err) {
      setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
    } finally {
      setLoading(false);
    }
  }, [courseId, canManage]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Debounce user search
  useEffect(() => {
    const t = setTimeout(() => setUserQDebounced(userQ), 250);
    return () => clearTimeout(t);
  }, [userQ]);

  useEffect(() => {
    if (!addOpen || targetType !== "user") return;
    void listUsers({ q: userQDebounced || undefined, page_size: 20 }).then((r) =>
      setUserResults(r.items),
    );
  }, [addOpen, targetType, userQDebounced]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload =
        targetType === "user"
          ? {
              target_type: "user" as const,
              target_user_id: selectedUserId!,
              due_at: moscowDateToUtcIso(dueAt),
            }
          : {
              target_type: "role" as const,
              target_role: selectedRole,
              due_at: moscowDateToUtcIso(dueAt),
            };
      if (targetType === "user" && selectedUserId === null) {
        alert("Выбери пользователя");
        return;
      }
      await createAssignment(courseId, payload);
      // Reset
      setAddOpen(false);
      setSelectedUserId(null);
      setSelectedUserLabel("");
      setUserQ("");
      setDueAt("");
      await reload();
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRevoke(a: AssignmentOut) {
    const label = a.target_user_full_name
      ? a.target_user_full_name
      : a.target_role
        ? `все ${ROLE_LABELS[a.target_role]}`
        : "назначение";
    if (!confirm(`Отозвать назначение «${label}»?`)) return;
    try {
      await revokeAssignment(a.id);
      await reload();
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  if (!canManage) return null;

  return (
    <section className="mt-8">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Назначения</h2>
        <button
          type="button"
          onClick={() => setAddOpen((s) => !s)}
          className="btn-secondary"
        >
          {addOpen ? "Отмена" : "+ Назначить"}
        </button>
      </div>

      {addOpen && (
        <form
          onSubmit={handleSubmit}
          className="mb-3 space-y-3 rounded-lg border border-slate-200 bg-white p-4"
        >
          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={targetType === "user"}
                onChange={() => setTargetType("user")}
              />
              Конкретному пользователю
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={targetType === "role"}
                onChange={() => setTargetType("role")}
              />
              Всем с ролью
            </label>
          </div>

          {targetType === "user" ? (
            <div className="space-y-2">
              <input
                type="search"
                placeholder="Поиск пользователя…"
                value={userQ}
                onChange={(e) => {
                  setUserQ(e.target.value);
                  setSelectedUserId(null);
                  setSelectedUserLabel("");
                }}
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                autoFocus
              />
              {selectedUserId !== null && (
                <div className="rounded bg-blue-50 px-3 py-1.5 text-sm text-blue-800">
                  Выбран: {selectedUserLabel}
                </div>
              )}
              {!selectedUserId && userResults.length > 0 && (
                <div className="max-h-44 overflow-y-auto rounded border border-slate-200">
                  {userResults.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => {
                        setSelectedUserId(u.id);
                        setSelectedUserLabel(`${u.full_name} (${u.login})`);
                      }}
                      className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                    >
                      <span className="text-slate-900">{u.full_name}</span>{" "}
                      <span className="font-mono text-xs text-slate-500">@{u.login}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as AcademyRole)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            >
              {ALL_ROLES.filter((r) => MANAGEABLE_TARGET_ROLES.includes(r)).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          )}

          <div className="flex items-center gap-2 text-sm">
            <label htmlFor="due" className="text-slate-700">
              Дедлайн (опц.):
            </label>
            <input
              id="due"
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary"
            >
              {submitting ? "…" : "Назначить"}
            </button>
          </div>
        </form>
      )}

      {error && (
        <div role="alert" className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-4 text-center text-sm text-slate-400">Загрузка…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          Курс пока никому не назначен.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between rounded-lg bg-white px-4 py-2 shadow-sm"
            >
              <div>
                {a.target_type === "user" ? (
                  <span className="text-sm text-slate-900">
                    {a.target_user_full_name ?? "—"}{" "}
                    <span className="font-mono text-xs text-slate-500">
                      @{a.target_user_login}
                    </span>
                  </span>
                ) : (
                  <span className="text-sm">
                    <span className="badge badge-neutral">
                      Все: {ROLE_LABELS[a.target_role as AcademyRole]}
                    </span>
                  </span>
                )}
                {a.due_at && (
                  <span className="ml-3 text-xs text-slate-500">
                    Дедлайн: {formatMoscowDate(a.due_at)}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleRevoke(a)}
                className="text-xs text-red-600 hover:underline"
              >
                Отозвать
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
